import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fireConfetti = () => {
  const colors = ["#6FB089", "#F4C95D", "#F4A28C", "#F4A23C"];
  const fire = (particleRatio, opts) =>
    confetti({ origin: { y: 0.6 }, colors, particleCount: Math.floor(180 * particleRatio), ...opts });
  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.9 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

export const WaitlistForm = ({ onJoined, idSuffix = "" }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (done && position) {
      const t = setTimeout(() => {}, 0);
      return () => clearTimeout(t);
    }
  }, [done, position]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { email });
      const { message, already_joined } = res.data;
      setDone(true);
      if (already_joined) {
        toast.success("Already on the list", { description: message });
      } else {
        toast.success("You're in!", { description: "We'll let you know when Coly is ready to download." });
        fireConfetti();
      }
      onJoined?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail[0]?.msg) {
        toast.error("Hmm, that email looks off", { description: detail[0].msg });
      } else {
        toast.error("Something went wrong", { description: "Please try again in a moment." });
      }
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid={`waitlist-success${idSuffix}`}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 16 }}
        className="bg-white rounded-full border border-coly-ink/10 px-2 py-1.5 flex items-center gap-3 max-w-[520px] w-full shadow-[0_8px_22px_rgba(47,58,44,0.06)] h-[60px]"
      >
        <div className="w-12 h-12 rounded-full bg-coly-green text-white flex items-center justify-center shrink-0">
          <Check className="w-5 h-5" strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-extrabold leading-tight truncate">You're on the list!</div>
          <div className="text-[12px] text-coly-ink-soft font-semibold truncate">
            We'll let you know when Coly is ready to download.
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={`waitlist-form${idSuffix}`}
      className="flex items-center gap-1.5 bg-white p-1.5 rounded-full border border-coly-ink/10 max-w-[520px] w-full h-[58px] sm:h-[60px]"
      style={{ boxShadow: "0 1px 0 rgba(47,58,44,0.04), 0 8px 22px rgba(47,58,44,0.06)" }}
    >
      <input
        data-testid={`waitlist-email-input${idSuffix}`}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        required
        className="flex-1 min-w-0 h-full bg-transparent border-0 outline-none px-3 sm:px-4 text-[14px] sm:text-base font-semibold text-coly-ink placeholder:text-coly-ink-soft/60"
      />
      <motion.button
        whileTap={{ scale: 0.97 }}
        whileHover={{ y: -1 }}
        data-testid={`waitlist-submit-btn${idSuffix}`}
        type="submit"
        disabled={loading}
        className="shrink-0 h-11 sm:h-12 px-4 sm:px-6 bg-coly-green text-white rounded-full font-extrabold text-[13px] sm:text-[15px] inline-flex items-center gap-1.5 sm:gap-2 disabled:opacity-70 whitespace-nowrap"
        style={{ boxShadow: "0 3px 0 #4F8E6A" }}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="hidden sm:inline">Joining…</span>
          </>
        ) : (
          <>
            <span className="sm:hidden">Join</span>
            <span className="hidden sm:inline">Join waitlist</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" />
              <path d="M13 5l7 7-7 7" />
            </svg>
          </>
        )}
      </motion.button>
    </form>
  );
};

export default WaitlistForm;
